import React from "react";
import { Link } from "react-router-dom";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { TextInput } from "@mantine/core";
import AuthScreenShell from "../../components/AuthScreenShell";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { ButtonLoader } from "../../components/Loader";

interface ForgotPasswordResponse {
  message?: string;
  detail?: string;
}

interface ForgotPasswordError {
  email?: string[] | string;
  error?: string;
  message?: string;
  [key: string]: any;
}

export default function ForgotPassword() {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value: string) => {
        if (!value.trim()) {
          return "Email is required";
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          return "Please enter a valid email address";
        }
        return null;
      },
    },
  });

  async function onSubmit(values: typeof form.values) {
    setIsSubmitting(true);

    try {
      const endpoint = Endpoints.GetForgotPassword();
      const response = await PerformRequest<ForgotPasswordResponse>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          email: values.email.trim(),
        },
        doNotUseAuthorization: true,
      });

      if (response.status === 200 || response.status === 204) {
        notifications.show({
          title: "Success",
          message:
            "If an account exists for this email, we've sent a password reset link.",
          color: "green",
        });
        form.reset();
      } else {
        const errorData = (response.data || {}) as ForgotPasswordError;
        let errorMessage = "Failed to send reset link. Please try again.";

        if (errorData?.email) {
          if (Array.isArray(errorData.email)) {
            errorMessage = errorData.email[0];
          } else {
            errorMessage = errorData.email;
          }
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.detail) {
          errorMessage = errorData.detail;
        }

        notifications.show({
          title: "Error",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      let errorMessage = "An error occurred. Please try again.";

      if (error?.response?.data) {
        const errorData = error.response.data as ForgotPasswordError;
        if (errorData?.email && Array.isArray(errorData.email)) {
          errorMessage = errorData.email[0];
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthScreenShell>
      <div className="auth-screen__header space-y-2">
        <h2 className="auth-screen__title">Forgot Password?</h2>
        <p className="auth-screen__desc">
          Enter your email address to
          <br />
          reset your password
        </p>
      </div>

      <form
        className="auth-form space-y-4"
        onSubmit={form.onSubmit(onSubmit)}
      >
        <TextInput
          label="Email Address"
          placeholder="Enter your email"
          type="email"
          autoComplete="email"
          disabled={isSubmitting}
          error={form.errors.email}
          {...form.getInputProps("email")}
        />

        <button
          className="button button--primary auth-screen__submit transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2 flex items-center justify-center gap-2"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <ButtonLoader color="white" />
              <span>Sending...</span>
            </>
          ) : (
            "Send reset link"
          )}
        </button>
      </form>
      <Link
        className="auth-screen__forgot inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2"
        to="/login"
      >
        Back to Login
      </Link>
    </AuthScreenShell>
  );
}
